import { TiktokAudio } from '@round/api';
import { ButtonShell, getTableMetaHelper, ScrollView, Skeleton, TableProps } from '@round/ui-kit';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { Table } from '@round/ui-kit';
import styles from '../../AudiosTable.module.css';
import { Tooltip, assets } from '@round/whitelabel-ui';
import { formatNumberToKNotation } from 'helpers';
import cn from 'classnames';

const { Cross: CrossIcon, InfoCircle: InfoIcon } = assets;

export type TiktokAudiosTableRow = TiktokAudio & {
    isStatic: boolean;
    video_count?: number | null;
};

type Props = Pick<TableProps<TiktokAudiosTableRow>, 'data'> & {
    onRemoveAudio: (audioId: number) => void;
    isLoading?: boolean;
    hasError?: boolean;
};

type Meta = Pick<Props, 'onRemoveAudio' | 'isLoading'>;

const getTableMeta = getTableMetaHelper<Meta>();

const TiktokAudiosTable = ({ data, onRemoveAudio, isLoading, hasError }: Props) => {
    const columns = useMemo<ColumnDef<TiktokAudiosTableRow, any>[]>(
        () => [
            {
                header: 'Audio Name',
                accessorKey: 'name',
                cell: ({ row: { original }, table }) => {
                    const { isLoading } = getTableMeta(table);
                    if (isLoading) {
                        return <Skeleton />;
                    }

                    const formattedTitle = original.title ? `${original.title} -` : '';

                    return `${formattedTitle} ${original.tiktok_id}`;
                },
            },
            {
                header: 'Creations',
                accessorKey: 'video_count',
                cell: ({ getValue, table }) => {
                    const { isLoading } = getTableMeta(table);

                    if (isLoading) {
                        return <Skeleton />;
                    }

                    return typeof getValue() === 'number' ? formatNumberToKNotation(getValue()) : '-';
                },
            },
            {
                header: '',
                id: 'remove',
                accessorKey: 'isStatic',
                cell: function RemoveCell({ getValue, row: { original }, table }) {
                    const [anchor, setAnchor] = useState<SVGSVGElement | null>(null);
                    const { isLoading, onRemoveAudio } = getTableMeta(table);
                    if (isLoading) {
                        return <Skeleton />;
                    }

                    return (
                        <div className={styles.removeCell}>
                            {!!getValue() ? (
                                <>
                                    <InfoIcon ref={setAnchor} className={styles.staticAudioTooltip} />
                                    <Tooltip anchorElement={anchor}>
                                        <Tooltip.Body>
                                            This audio is linked to the spotify song and cannot be removed
                                        </Tooltip.Body>
                                    </Tooltip>
                                </>
                            ) : (
                                <ButtonShell
                                    onClick={() => onRemoveAudio(original.id)}
                                    className={styles.removeButton}
                                    type="button"
                                >
                                    <CrossIcon />
                                </ButtonShell>
                            )}
                        </div>
                    );
                },
            },
        ],
        []
    );

    const meta: Meta = {
        onRemoveAudio,
        isLoading,
    };

    // skeletons are rendered in table cells,
    // so we need some rows to display skeletons on initial loading
    const initialLoading = !data.length && isLoading;
    const emptyRows = new Array(6).fill({}) as TiktokAudiosTableRow[];
    const rows = initialLoading ? emptyRows : data;

    return (
        <ScrollView className={styles.tableContainer}>
            <Table
                data={rows}
                columns={columns}
                meta={meta}
                className={styles.table}
                getRowClassName={({ original }) => (original.isStatic ? styles.static : '')}
                noDataLabel={
                    <div className={cn(styles.noDataLabel, { [styles.error]: hasError })}>
                        {hasError ? 'Something went wrong' : 'No audios selected'}
                    </div>
                }
            />
        </ScrollView>
    );
};

export default TiktokAudiosTable;
